/**
 * 服务端时钟：用 GET /v1/market/status 的 serverTime 校准，
 * 再以 performance.now() 推进，避免设备时钟漂移影响持仓倒计时。
 */

import { getMarketStatus } from '@/api/markets-api';
import { applyMarketStatusPayload } from '@/common/utils/marketStatus';

let serverAnchorMs = 0;
let localAnchorMs = 0;
let synced = false;
let syncing = false;
let syncPromise: Promise<boolean> | null = null;
let lastSyncAt = 0;
/** 已校准后短窗复用锚点，避免进页 onShow + timers 连打 /v1/market/status */
const CLOCK_SYNC_TTL_MS = 45_000;

const nowPerf = (): number => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  // 非浏览器兜底：仍用单调性较差的 Date，但 sync 后尽量少依赖墙钟差值
  return Date.now();
};

export const isClockSynced = (): boolean => synced;

export const isClockSyncing = (): boolean => syncing;

/**
 * 拉取 serverTime，按半程 RTT 校正锚点。
 * 并发调用复用同一次请求；已同步且在 TTL 内直接返回（可用 force 强制）。
 */
export const syncClock = async (opts?: { force?: boolean }): Promise<boolean> => {
  if (syncPromise) return syncPromise;
  if (!opts?.force && synced && Date.now() - lastSyncAt < CLOCK_SYNC_TTL_MS) {
    return true;
  }

  syncing = true;
  syncPromise = (async () => {
    try {
      const start = nowPerf();
      const data = await getMarketStatus();
      const end = nowPerf();
      // 顺带缓存 symbols，供交易列表点击校验
      applyMarketStatusPayload(data);
      const serverMs = Date.parse(String(data?.serverTime || ''));
      if (!Number.isFinite(serverMs)) {
        return synced;
      }
      serverAnchorMs = serverMs + (end - start) / 2;
      localAnchorMs = end;
      synced = true;
      lastSyncAt = Date.now();
      return true;
    } catch {
      return synced;
    } finally {
      syncing = false;
      syncPromise = null;
    }
  })();

  return syncPromise;
};

/** 当前估计的服务端毫秒时间；未校时时返回 NaN（调用方应显示「同步时间中」） */
export const serverNowMs = (): number => {
  if (!synced) return Number.NaN;
  return serverAnchorMs + nowPerf() - localAnchorMs;
};

/** settleAt 相对服务端时钟的剩余毫秒；未校时或无效时间为 NaN */
export const remainingMsUntil = (settleAt: string | number | Date | null | undefined, now = serverNowMs()): number => {
  if (settleAt == null || settleAt === '') return Number.NaN;
  const settle =
    typeof settleAt === 'number' ? settleAt : Date.parse(String(settleAt));
  if (!Number.isFinite(settle)) return Number.NaN;
  if (!Number.isFinite(now)) return Number.NaN;
  return Math.max(0, settle - now);
};

export const remainingSecondsUntil = (
  settleAt: string | number | Date | null | undefined
): number => {
  const ms = remainingMsUntil(settleAt);
  if (!Number.isFinite(ms)) return Number.NaN;
  return Math.ceil(ms / 1000);
};

/**
 * 核对 60s 订单：settleAt - acceptedAt 应为约 60000ms。
 * 偏差过大时打 warn，便于把原始响应对给后端。
 */
export const assertRolling60sSettleWindow = (
  order: { settleAt?: string; acceptedAt?: string; interval?: string; orderMode?: string },
  raw?: unknown
): void => {
  // ORDINARY_ROLLING_60S 名含 60S 仅为兼容；实际时长以 interval 为准（§2.1）
  const interval = String(order.interval || '1m').toLowerCase().trim();
  const is60s = interval === '1m' || interval === '60s' || interval === '';
  if (!is60s || !order.settleAt || !order.acceptedAt) return;

  const settle = Date.parse(order.settleAt);
  const accepted = Date.parse(order.acceptedAt);
  if (!Number.isFinite(settle) || !Number.isFinite(accepted)) return;

  const delta = settle - accepted;
  if (Math.abs(delta - 60_000) > 1_500) {
    console.warn(
      '[serverClock] rolling 60s settle window mismatch:',
      { deltaMs: delta, expectedMs: 60_000, settleAt: order.settleAt, acceptedAt: order.acceptedAt },
      raw ?? order
    );
  }
};
